/**
 * @jsxRuntime classic
 * @jsx jsx
 */
import { useMemo } from 'react';

import { css, cssMap, jsx } from '@compiled/react';
import { FormattedMessage } from 'react-intl';

import Avatar from '@atlaskit/avatar/Avatar';
import { Box, Flex } from '@atlaskit/primitives/compiled';
import Spinner from '@atlaskit/spinner/spinner';
import TabPanel from '@atlaskit/tabs/tab-panel';
import { token } from '@atlaskit/tokens';

import { messages } from '../shared/i18n';
import { type ReactionSummary, type ProfileCardWrapper } from '../types';
import { AgentReactionAvatar } from './AgentReactionAvatar';

const userListStyle = css({
	listStyle: 'none',
	marginTop: token('space.200'),
	padding: 0,
	textAlign: 'left',
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors -- Ignored via go/DSP-18766
	li: {
		color: `${token('color.text')}`,
		font: token('font.body'),
	},
});

const userStyle = css({
	display: 'flex',
	alignItems: 'center',
	paddingTop: token('space.050'),
	paddingBottom: token('space.050'),
	paddingLeft: 0,
	paddingRight: 0,
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors -- Ignored via go/DSP-18766
	'> span': {
		marginLeft: token('space.200'),
	},
});

const styles = cssMap({
	userDescriptionStyle: {
		marginLeft: token('space.150'),
	},

	profileWrapperStyle: {
		marginLeft: token('space.0'),
	},

	reactionViewStyle: {
		marginTop: token('space.150'),
		minHeight: '300px',
		minWidth: '550px',
	},

	centerSpinnerStyle: {
		display: 'flex',
		justifyContent: 'center',
		alignItems: 'center',
		height: '100%',
		marginTop: token('space.300'),
	},
});

export interface ReactionViewProps {
	/**
	 * Optional ProfileWrapper component to show profile card on hover
	 */
	ProfileCardWrapper?: ProfileCardWrapper;
	/**
	 * Selected reaction to get user data from
	 */
	reaction: ReactionSummary;
}

export const ReactionView = ({ reaction, ProfileCardWrapper }: ReactionViewProps): JSX.Element => {
	const alphabeticalNames = useMemo(() => {
		const reactionObj = reaction;

		return (
			reactionObj.users
				?.slice()
				.sort((a, b) =>
					(a.agent?.name ?? a.displayName).localeCompare(b.agent?.name ?? b.displayName),
				) || []
		);
	}, [reaction]);

	return (
		<TabPanel>
			<Flex direction="column" xcss={styles.reactionViewStyle}>
				{alphabeticalNames.length === 0 ? (
					<Box xcss={styles.centerSpinnerStyle}>
						<Spinner size="large" interactionName="reactions-spinner" />
					</Box>
				) : (
					<ul css={userListStyle}>
						{alphabeticalNames.map((user) => {
							const profile = user.profilePicture?.path;
							return (
								<li css={userStyle} key={user.id}>
									{user.agent ? (
										<AgentReactionAvatar
											user={user}
											agent={user.agent}
											ProfileCardWrapper={ProfileCardWrapper}
										/>
									) : ProfileCardWrapper && user.accountId ? (
										<Box xcss={styles.profileWrapperStyle}>
											<ProfileCardWrapper
												userId={user.accountId}
												isAnonymous={false}
												canViewProfile
												position="left-start"
											>
												<Avatar size="medium" src={profile} testId="profile" />
											</ProfileCardWrapper>
										</Box>
									) : (
										<Avatar size="medium" src={profile} testId="profile" />
									)}
									<Flex xcss={styles.userDescriptionStyle}>
										<div>
											{user.agent ? (
												<FormattedMessage
													{...messages.agentWithPerson}
													values={{ agentName: user.agent.name, personName: user.displayName }}
												/>
											) : (
												user.displayName
											)}
										</div>
									</Flex>
								</li>
							);
						})}
					</ul>
				)}
			</Flex>
		</TabPanel>
	);
};
